import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import EventAttachmentsDisplay from './EventAttachmentsDisplay';
import EventSenderAnalytics from './EventSenderAnalytics';
import ExpenseTrackerPanel from './ExpenseTrackerPanel';
import ExpenseSharePanel from './ExpenseSharePanel';
import { isExpenseSharingEvent, isExpenseTrackingEvent } from '../constants/expenseFields';
import { isSettlementEntry } from '../utils/expenseShareUtils';
import {
  formatEventSchedule,
  getStatusLabel,
  getTypeMeta,
  PARTICIPANT_STATUS_BADGE,
  PARTICIPANT_STATUS_LABEL,
  resolveEventLocation,
} from '../utils/eventTimelineUtils';
import { isPollEvent } from '../utils/pollUtils';
import PollVoteBlock from './PollVoteBlock';
import ParticipantAvatarRow from './ParticipantAvatarRow';

const LocationIcon = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const CalendarIcon = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const EventDetailModal = ({
  isOpen,
  onClose,
  eventInfo,
  currentUserId,
  saving = false,
  error = '',
  onRespond,
  onEditRsvp,
  onEditEvent,
  onCloneEvent,
  onCloseEvent,
  onReopenEvent,
  onPollVote,
  cloning = false,
  responseOptions = [],
  canRespond = false,
  myStatus = 'PENDING',
  hasDecided = false,
  myRsvpSummary = '',
}) => {
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [addingEntry, setAddingEntry] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [formKind, setFormKind] = useState('expense');

  const openExpenseForm = (entry = null) => {
    setFormKind(isSettlementEntry(entry) ? 'settle' : 'expense');
    setEditingEntry(entry);
    setAddingEntry(true);
  };

  const openSettleForm = () => {
    setFormKind('settle');
    setEditingEntry(null);
    setAddingEntry(true);
  };

  const closeExpenseForm = () => {
    setAddingEntry(false);
    setEditingEntry(null);
    setFormKind('expense');
  };

  useEffect(() => {
    if (!isOpen) {
      setShowAnalytics(false);
      setAddingEntry(false);
      setEditingEntry(null);
      setFormKind('expense');
    }
  }, [isOpen, eventInfo?.eventId]);

  if (!isOpen || !eventInfo?.eventId) return null;

  const typeMeta = getTypeMeta(eventInfo.eventType);
  const scheduleLabel = formatEventSchedule(eventInfo);
  const { label: locationLabel, link: locationLink } = resolveEventLocation(eventInfo);
  const participants = eventInfo.participants || [];
  const sameId = (a, b) => a != null && b != null && String(a) === String(b);
  const isCreator = sameId(eventInfo.creatorId, currentUserId);
  const status = String(eventInfo.status || '').toUpperCase();
  const isClosed = status === 'CLOSED';
  const isPoll = isPollEvent(eventInfo);
  const canModify = isCreator && !['CANCELLED', 'EXPIRED', 'CLOSED'].includes(status);
  const canClose = isCreator && !['DRAFT', 'CANCELLED', 'CLOSED'].includes(status);
  const canReopen = isPoll && isCreator && isClosed && typeof onReopenEvent === 'function';
  const showExpensePanel = isExpenseTrackingEvent(eventInfo.eventType) || isExpenseSharingEvent(eventInfo.eventType);

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[1300] flex items-center justify-center p-3 sm:p-6 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={eventInfo.name || 'Event details'}
    >
      <div
        className={`relative flex flex-col overflow-hidden rounded-3xl shadow-2xl bg-white dark:bg-gray-900 animate-[eventModalIn_0.28s_ease-out] ${
          showAnalytics
            ? 'w-[80vw] max-w-[1400px] h-[min(88vh,900px)]'
            : addingEntry
              ? 'w-full max-w-md h-[min(90vh,760px)]'
              : 'w-full max-w-md max-h-[90vh]'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <style>{`
          @keyframes eventModalIn {
            from { opacity: 0; transform: scale(0.97) translateY(10px); }
            to { opacity: 1; transform: scale(1) translateY(0); }
          }
        `}</style>

        {addingEntry && showExpensePanel ? (
          <>
            <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-200 dark:border-gray-800 shrink-0">
              <button
                type="button"
                onClick={closeExpenseForm}
                className="text-sm font-semibold text-gray-600 dark:text-gray-300 px-2 py-1"
              >
                Back
              </button>
              <h2 className="text-base font-bold text-gray-900 dark:text-white">
                {formKind === 'settle' ? (editingEntry ? 'Edit settlement' : 'Record settlement') : (editingEntry ? 'Edit entry' : 'Add entry')}
              </h2>
              <span className="w-12" />
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4">
              {isExpenseTrackingEvent(eventInfo.eventType) ? (
                <ExpenseTrackerPanel
                  eventId={eventInfo.eventId}
                  currentUserId={currentUserId}
                  isCreator={isCreator}
                  configuration={eventInfo.configuration || {}}
                  mode="form"
                  initialEntry={editingEntry}
                  onSaved={closeExpenseForm}
                />
              ) : (
                <ExpenseSharePanel
                  eventId={eventInfo.eventId}
                  currentUserId={currentUserId}
                  isCreator={isCreator}
                  configuration={eventInfo.configuration || {}}
                  participants={participants}
                  mode={formKind === 'settle' ? 'settle' : 'form'}
                  initialEntry={editingEntry}
                  onSaved={closeExpenseForm}
                />
              )}
            </div>
          </>
        ) : (
          <>
        <div className="relative shrink-0 bg-gradient-to-r from-blue-700 via-blue-600 to-sky-500 px-6 py-5 text-white">
          <div className="absolute inset-0 opacity-25 pointer-events-none" style={{
            backgroundImage: 'radial-gradient(circle at 12% 80%, white 0%, transparent 42%), radial-gradient(circle at 88% 20%, white 0%, transparent 36%)',
          }} />
          <button
            type="button"
            onClick={onClose}
            className="absolute top-3 right-4 z-10 w-9 h-9 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-white transition-colors"
            aria-label="Close"
          >
            ✕
          </button>
          <div className={`relative z-[1] ${showAnalytics ? 'pr-12 flex flex-wrap items-end justify-between gap-3' : 'pr-10'}`}>
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-2xl" aria-hidden="true">{typeMeta.icon}</span>
                <span className="text-xs font-bold uppercase tracking-widest bg-white/20 px-2.5 py-1 rounded-full">
                  {typeMeta.label}
                </span>
              </div>
              <h2 className="text-3xl font-bold leading-tight">{eventInfo.name || 'Untitled event'}</h2>
              {eventInfo.creatorName && (
                <p className="text-sm text-white/90 mt-1.5 font-medium">
                  {eventInfo.creatorName}
                </p>
              )}
              {eventInfo.startDate && (
                <p className="text-sm text-white/80 mt-0.5">
                  {scheduleLabel}
                </p>
              )}
              {!(isPoll && String(eventInfo.status || '').toUpperCase() === 'WAITING_FOR_RESPONSE') && (
                <p className="text-sm text-white/80 mt-1 font-medium">{getStatusLabel(eventInfo.status)}</p>
              )}
            </div>
            {isCreator && (
              <button
                type="button"
                onClick={() => setShowAnalytics((open) => !open)}
                className="text-xs font-semibold underline underline-offset-2 text-white/95 hover:text-white"
              >
                {showAnalytics ? 'Back to event' : 'Analytics'}
              </button>
            )}
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto">
          <div className="p-5 sm:p-6 space-y-5">
            {isCreator && showAnalytics && (
              <EventSenderAnalytics
                eventId={eventInfo.eventId}
                eventName={eventInfo.name}
                fallbackParticipants={participants}
                rsvpFields={eventInfo.rsvpFields}
                eventType={eventInfo.eventType}
              />
            )}

            {!showAnalytics && (
            <>
            {isPoll && (
              <PollVoteBlock
                eventInfo={eventInfo}
                currentUserId={currentUserId}
                canVote={canRespond}
                saving={saving}
                onVote={onPollVote}
                showVoters
              />
            )}

            {!isPoll && (
            <div className="space-y-3">
              {eventInfo.startDate && (
              <div className="flex items-start gap-3 p-3 rounded-xl bg-blue-50 dark:bg-blue-950/30 border border-blue-100 dark:border-blue-900/40">
                <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-gradient-to-br from-blue-600 to-sky-500 flex items-center justify-center text-white">
                  <CalendarIcon className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400">When</p>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white mt-0.5">{scheduleLabel}</p>
                  {eventInfo.timezone && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{eventInfo.timezone}</p>
                  )}
                </div>
              </div>
              )}

              <div className="flex items-start gap-3 p-3 rounded-xl bg-rose-50 dark:bg-rose-950/20 border border-rose-100 dark:border-rose-900/40">
                <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-rose-500 flex items-center justify-center text-white">
                  <LocationIcon className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] font-bold uppercase tracking-wide text-rose-600/80 dark:text-rose-300/80">Location</p>
                  {locationLabel ? (
                    <p className="text-sm font-semibold text-gray-900 dark:text-white mt-0.5">{locationLabel}</p>
                  ) : (
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">No location set</p>
                  )}
                  {locationLink && (
                    <a
                      href={locationLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-rose-600 dark:text-rose-400 hover:underline break-all mt-1 inline-block"
                    >
                      Open in maps / join link
                    </a>
                  )}
                </div>
              </div>
            </div>
            )}

            {eventInfo.description && (
              <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">{eventInfo.description}</p>
            )}

            <EventAttachmentsDisplay attachments={eventInfo.attachments} />

            {isExpenseTrackingEvent(eventInfo.eventType) && (
              <ExpenseTrackerPanel
                eventId={eventInfo.eventId}
                currentUserId={currentUserId}
                isCreator={isCreator}
                configuration={eventInfo.configuration || {}}
                readOnly={isClosed || status === 'CANCELLED'}
                onAddEntry={() => openExpenseForm()}
                onEditEntry={(entry) => openExpenseForm(entry)}
              />
            )}

            {isExpenseSharingEvent(eventInfo.eventType) && (
              <ExpenseSharePanel
                eventId={eventInfo.eventId}
                currentUserId={currentUserId}
                isCreator={isCreator}
                configuration={eventInfo.configuration || {}}
                participants={participants}
                readOnly={isClosed || status === 'CANCELLED'}
                eventName={eventInfo.name}
                onAddEntry={() => openExpenseForm()}
                onSettle={openSettleForm}
                onEditEntry={(entry) => openExpenseForm(entry)}
              />
            )}

            {eventInfo.creatorName && (
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 px-1">
                <span className="w-8 h-8 rounded-full bg-violet-100 dark:bg-violet-900/40 flex items-center justify-center text-violet-600 dark:text-violet-300 text-xs font-bold">
                  {(eventInfo.creatorName || '?').charAt(0)}
                </span>
                <span>Organized by <strong className="text-gray-900 dark:text-white">{eventInfo.creatorName}</strong></span>
              </div>
            )}

            {eventInfo.tags?.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {eventInfo.tags.map((tag) => (
                  <span key={tag} className="text-[10px] font-semibold px-2.5 py-1 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
                    #{tag}
                  </span>
                ))}
              </div>
            )}

            {!isPoll && participants.length > 0 && (
              <ParticipantAvatarRow
                participants={participants}
                currentUserId={currentUserId}
              />
            )}
            </>
            )}

            {error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}
          </div>
        </div>

        {(canRespond && responseOptions.length > 0) || (canModify && onEditEvent) || onCloneEvent || canClose || canReopen ? (
          <div className="shrink-0 border-t border-gray-200 dark:border-gray-700 bg-gray-50/90 dark:bg-gray-900/95 px-5 sm:px-6 py-3.5 space-y-2">
            {canRespond && responseOptions.length > 0 && (
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap items-center gap-2">
                  {hasDecided && (
                    <>
                      <span className={`inline-flex px-2.5 py-1 rounded-full text-[11px] font-semibold ${PARTICIPANT_STATUS_BADGE[myStatus] || PARTICIPANT_STATUS_BADGE.PENDING}`}>
                        Your response: {PARTICIPANT_STATUS_LABEL[myStatus] || myStatus}
                      </span>
                      {myRsvpSummary && (
                        <span className="text-[11px] text-gray-500 dark:text-gray-400">{myRsvpSummary}</span>
                      )}
                      {onEditRsvp && (
                        <button
                          type="button"
                          disabled={saving}
                          onClick={() => onEditRsvp()}
                          className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-lg border border-emerald-300 dark:border-emerald-700 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-950/30 disabled:opacity-50 transition-colors"
                        >
                          Edit RSVP
                        </button>
                      )}
                    </>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {responseOptions.map((opt) => (
                    <button
                      key={opt.id}
                      type="button"
                      disabled={saving || (hasDecided && myStatus === opt.id)}
                      onClick={() => onRespond?.(opt.id)}
                      className={`text-sm font-semibold px-4 py-2.5 rounded-xl disabled:opacity-50 transition-all hover:scale-[1.02] active:scale-[0.98] ${opt.className}`}
                    >
                      {hasDecided && myStatus !== opt.id ? `Change to ${opt.label}` : opt.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {(canModify && (onEditEvent || onCloneEvent) || onCloneEvent || canClose || canReopen) && (
              <div className="flex flex-wrap gap-2">
                {canModify && onEditEvent && (
                  <button
                    type="button"
                    onClick={() => { onClose(); onEditEvent(eventInfo.eventId); }}
                    className="text-xs font-semibold px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-white dark:hover:bg-gray-800"
                  >
                    Edit event
                  </button>
                )}
                {onCloneEvent && (
                  <button
                    type="button"
                    onClick={() => { onClose(); onCloneEvent(eventInfo.eventId); }}
                    disabled={cloning}
                    className="text-xs font-semibold px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-white dark:hover:bg-gray-800 disabled:opacity-50"
                  >
                    {cloning ? 'Cloning…' : 'Clone event'}
                  </button>
                )}
                {canClose && onCloseEvent && (
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() => onCloseEvent()}
                    className="text-xs font-semibold px-3 py-2 rounded-lg border border-rose-300 dark:border-rose-700 text-rose-700 dark:text-rose-300 hover:bg-rose-50 dark:hover:bg-rose-950/30 disabled:opacity-50"
                  >
                    {saving ? 'Closing…' : (isPoll ? 'Close poll' : 'Close event')}
                  </button>
                )}
                {canReopen && (
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() => onReopenEvent()}
                    className="text-xs font-semibold px-3 py-2 rounded-lg border border-emerald-300 dark:border-emerald-700 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-950/30 disabled:opacity-50"
                  >
                    {saving ? 'Reopening…' : 'Reopen poll'}
                  </button>
                )}
              </div>
            )}
          </div>
        ) : null}
          </>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default EventDetailModal;
